// /account — the page a signed-in user changes their own details on.
//
// PHP had this as account/profile.php over three ajax handlers, and the rebuild
// shipped without it entirely: every column existed and none was reachable, so a
// user could not change their password, their timezone or their name, and could
// never add a phone number at all. That last one killed every SMS path including
// the day-of ping, which is the product's differentiator. See
// docs/migration-gaps.md items 1 and 2.
//
// Six independent sections, each saving on its own, because the endpoint writes
// only the keys it is sent. That is deliberate: one big save button would make
// changing a name and changing a phone number the same act, and one of those
// texts you a code.
//
// The two that are not just form fields:
//
//   • PASSWORD signs you out everywhere, including here. The button says so
//     before you press it, and afterwards this view routes to /login rather than
//     leaving a dead session on screen.
//   • EMAIL is a two-step flow for the same reason as phone: an address is a
//     login identifier, and `findAccountByIdentifier()` matches ANY row in
//     site_user_email, so an unproven address must never be written there. The
//     pending state lives in a signed token, not the database.
//   • PHONE is a two-step flow, because `phone` is a login identifier and an
//     unproven number must never land in it. Typing a number texts a code; the
//     number is not on the account until the code comes back.
//   • DELETE closes the account (App Store Guideline 5.1.1(v)) and is refused
//     while you own a group with other members in it. It sits last, in its own
//     card, and asks for the password plus a typed phrase.

import {
  ApiClientError,
  changePassword,
  fetchBlocked,
  unblockUser,
  deleteAccount,
  fetchMe,
  logout,
  confirmEmailChange,
  removePhone,
  startEmailChange,
  startPhoneVerification,
  updateProfile,
  verifyPhone,
} from '../api/client.js';
import { navigate, type View } from '../router.js';
import { invalidateChrome, refreshChrome } from '../chrome.js';
import { el } from '../dom.js';
import {
  DELETE_ACCOUNT_PHRASE,
  ErrorCodes,
  type BlockingGroup,
  type CurrentUser,
} from '@msc/shared';
import { tzPicker } from './tzPicker.js';
import { passwordReveal } from './passwordReveal.js';
import { avatarPicker } from './avatarPicker.js';

export const accountView: View = async (outlet) => {
  const main = el('main', { class: 'page account-page' }, [
    el('p', { class: 'muted' }, 'Loading'),
  ]);
  outlet.append(main);

  let me: CurrentUser;
  try {
    me = await fetchMe();
  } catch (err) {
    if (err instanceof ApiClientError && err.status === 401) {
      navigate('/login', true);
      return;
    }
    main.replaceChildren(el('p', { class: 'form-error' }, 'Could not load your account.'));
    return;
  }

  // Re-rendered in place after any change, so every section shows the same truth
  // — verifying a phone number, for instance, also clears the pending row that
  // the phone section is displaying.
  function render(user: CurrentUser): void {
    // The header shows this user's photo and first name, and `currentUser()`
    // caches both. Refreshing here rather than in each card covers every path
    // that changes them -- the avatar upload, the avatar removal, and the
    // profile save -- instead of the two someone remembered.
    refreshChrome();
    // Logout moved here from the header in the 2026-10 nav rework: the header
    // lost its row of buttons, and this is the page the avatar leads to.
    const signOut = el('button', { type: 'button', class: 'btn btn-secondary' }, 'Log out');
    signOut.addEventListener('click', () => {
      signOut.setAttribute('disabled', 'disabled');
      void logout().then(() => {
        invalidateChrome();
        navigate('/login', true);
      });
    });
    main.replaceChildren(
      el('div', { class: 'account-head' }, [el('h1', {}, 'Your account'), signOut]),
      // The address used to be stated here. It now lives on the Email address
      // card, which owns it and can change it -- two copies would disagree the
      // moment one of them was stale.
      el('p', { class: 'muted small' }, [
        'Notification channels are under ',
        el('a', { href: '/settings/notifications' }, 'Notifications'),
        '.',
      ]),
      // One grid, not a stack. On a phone it is a single column and reads
      // exactly as it did before; from 48rem up it flows into as many columns
      // as fit, which is what stops five cards running down the middle of a
      // wide screen with white space either side. The danger card spans the
      // full width at the bottom rather than becoming a peer of the others.
      el('div', { class: 'account-grid' }, [
        avatarCard(user, render),
        profileCard(user, render),
        emailCard(user, render),
        phoneCard(user, render),
        blockedCard(),
        passwordCard(),
        deleteCard(),
      ]),
      el('p', { class: 'muted small' }, [
        el('a', { href: '/privacy' }, 'Privacy Policy'),
        ' · ',
        el('a', { href: '/terms' }, 'Terms and Conditions'),
      ]),
    );
  }

  render(me);
};

// --- Blocked members -------------------------------------------------------
//
// The only place a block can be found again. A block is silent by design -- the
// other person is never told -- which also means the person who made it has no
// reminder anywhere else. Without this card, "why can I not see Dave's posts"
// has no answer short of remembering.
//
// Loads its own data rather than taking it from CurrentUser: the list is
// unbounded and almost always empty, so putting it in the payload every /me
// request pays for it on every page load.

function blockedCard(): HTMLElement {
  const list = el('div', {}, [el('p', { class: 'muted small' }, 'Loading…')]);
  // card-wide, like Password and Delete. Four one-column cards (photo, profile,
  // email, phone) tile into two clean rows; a fifth would sit alone with a hole
  // beside it. It also groups the three cards that are about safety rather than
  // settings, which is the right reading.
  const card = el('section', { class: 'card card-wide' }, [
    el('h2', {}, 'Blocked members'),
    el(
      'p',
      { class: 'muted small' },
      'You will not see each other’s posts, chat messages or photos. ' +
        'Event announcements are unaffected, and they are not told.',
    ),
    list,
  ]);

  const paint = async (): Promise<void> => {
    try {
      const res = await fetchBlocked();
      if (res.blocked.length === 0) {
        list.replaceChildren(
          el('p', { class: 'muted small' }, 'You have not blocked anyone.'),
        );
        return;
      }
      list.replaceChildren(
        el(
          'ul',
          { class: 'plain-list' },
          res.blocked.map((b) => {
            const undo = el('button', { type: 'button', class: 'btn btn-link' }, 'Unblock');
            undo.addEventListener('click', async () => {
              undo.setAttribute('disabled', 'disabled');
              try {
                await unblockUser(b.userId);
                await paint();
              } catch {
                undo.removeAttribute('disabled');
              }
            });
            return el('li', { class: 'blocked-row' }, [
              b.avatarThumbUrl
                ? el('img', { class: 'account-avatar', src: b.avatarThumbUrl, alt: '' })
                : el('span', {}, ''),
              el('a', { href: `/users/${b.userId}` }, b.name || `Member ${b.userId}`),
              undo,
            ]);
          }),
        ),
      );
    } catch {
      list.replaceChildren(
        el('p', { class: 'form-error' }, 'Could not load your blocked members.'),
      );
    }
  };

  void paint();
  return card;
}

// --- Email address ---------------------------------------------------------
//
// Two states, and the second is the whole point: after a request the card shows
// "check your inbox" rather than pretending the address has changed. Nothing has
// -- the server writes nothing until the emailed link is followed, and the
// current address keeps working until then. Telling the user that explicitly is
// what stops them assuming the change failed when the old address still signs in.

function emailCard(user: CurrentUser, done: (u: CurrentUser) => void): HTMLElement {
  const error = el('p', { class: 'form-error', hidden: true });
  const sent = el('p', { class: 'form-ok', hidden: true });
  const submit = el('button', { type: 'submit', class: 'btn' }, 'Send confirmation link');

  const email = el('input', {
    id: 'ac-email',
    name: 'email',
    type: 'email',
    autocomplete: 'email',
    maxlength: '191',
    required: true,
    placeholder: 'you@example.com',
  }) as HTMLInputElement;

  const form = el('form', { method: 'post', action: '/account', novalidate: true }, [
    el('h2', {}, 'Email address'),
    el('p', { class: 'muted small' }, [
      'Currently ',
      el('strong', {}, user.email || '(none on file)'),
      '. This is also how you sign in.',
    ]),
    el('label', { for: 'ac-email' }, 'New email address'),
    email,
    el(
      'p',
      { class: 'muted small' },
      'We send a confirmation link to the new address. Nothing changes until you ' +
        'open it, and your current address keeps working in the meantime.',
    ),
    error,
    sent,
    submit,
  ]) as HTMLFormElement;

  form.addEventListener('submit', async (ev) => {
    ev.preventDefault();
    error.hidden = true;
    sent.hidden = true;
    const value = email.value.trim();
    if (value === '') {
      error.textContent = 'Enter the new email address.';
      error.hidden = false;
      return;
    }
    submit.setAttribute('disabled', 'disabled');
    try {
      const res = await startEmailChange(value);
      sent.textContent = res.message;
      sent.hidden = false;
      email.value = '';
      // Deliberately NOT calling done(): re-rendering would wipe the "check your
      // inbox" line, which is the only feedback there is. The account itself has
      // not changed, so there is nothing to re-render for.
    } catch (err) {
      error.textContent = message(err);
      error.hidden = false;
    } finally {
      submit.removeAttribute('disabled');
    }
  });

  return el('section', { class: 'card' }, [form]);
}

/**
 * The landing page for the emailed link: `/email-change?token=...`.
 *
 * A separate view rather than a branch of the account page, because the person
 * arriving here clicked a link in their mail client and may be on a different
 * device -- possibly signed out. `confirmEmailChange` requires auth, so a
 * signed-out arrival is told to sign in and open the link again rather than
 * being shown a silent failure.
 */
export const emailChangeView: View = async (outlet) => {
  const main = el('main', { class: 'page' }, [el('p', { class: 'muted' }, 'Confirming')]);
  outlet.append(main);

  const token = new URLSearchParams(location.search).get('token') ?? '';
  if (token === '') {
    main.replaceChildren(
      el('h1', {}, 'Email change'),
      el('p', { class: 'form-error' }, 'That link is missing its confirmation code.'),
      el('p', {}, [el('a', { href: '/account' }, 'Back to your account')]),
    );
    return;
  }

  try {
    const user = await confirmEmailChange(token);
    // The header shows the address on some surfaces and caches the identity.
    refreshChrome();
    main.replaceChildren(
      el('h1', {}, 'Email address changed'),
      el('p', {}, [
        'Your account now uses ',
        el('strong', {}, user.email),
        '. Sign in with that address from now on.',
      ]),
      el('p', { class: 'muted small' }, 'We let your previous address know as well.'),
      el('p', {}, [el('a', { href: '/account' }, 'Back to your account')]),
    );
  } catch (err) {
    main.replaceChildren(
      el('h1', {}, 'Email change'),
      el('p', { class: 'form-error' }, message(err)),
      el(
        'p',
        { class: 'muted small' },
        'If you are signed out, sign in with your CURRENT address first and then ' +
          'open the link again. It still works.',
      ),
      el('p', {}, [el('a', { href: '/account' }, 'Back to your account')]),
    );
  }
};

// --- Photo -----------------------------------------------------------------
//
// One image is uploaded; the server derives the 256px square the site header
// renders. See server/src/users/avatar.ts for why the thumbnail is not made in
// the browser.

function avatarCard(user: CurrentUser, done: (u: CurrentUser) => void): HTMLElement {
  return el('section', { class: 'card' }, [
    el('h2', {}, 'Your photo'),
    el(
      'p',
      { class: 'muted small' },
      'Shown beside your name in the header, and on your profile. ' +
        'JPEG, PNG, WebP or GIF.',
    ),
    avatarPicker(user, done),
  ]);
}

// --- Profile ---------------------------------------------------------------

function profileCard(user: CurrentUser, done: (u: CurrentUser) => void): HTMLElement {
  const error = el('p', { class: 'form-error', hidden: true });
  const ok = el('p', { class: 'form-ok', hidden: true }, 'Saved.');
  const submit = el('button', { type: 'submit', class: 'btn' }, 'Save profile');

  const firstName = textInput('ac-first', 'firstName', user.firstName, 100);
  const lastName = textInput('ac-last', 'lastName', user.lastName, 100);

  // Blank means "use the site zone", so `autodetect` is deliberately off: filling
  // it from the browser would pin a zone the user never chose. Same rule as the
  // event form. The inherit row names the SITE zone here, not a group's — see
  // shared/src/time.ts on the two chains.
  const zone = tzPicker({
    name: 'timeZone',
    value: user.timeZone,
    inheritLabel: 'Site default',
    id: 'ac-tz',
  });

  const form = el('form', { method: 'post', action: '/account', novalidate: true }, [
    el('h2', {}, 'Profile'),
    el('label', { for: 'ac-first' }, 'First name'),
    firstName,
    el('label', { for: 'ac-last' }, 'Last name'),
    lastName,
    el('label', { for: 'ac-tz' }, 'Your timezone'),
    zone.root,
    el(
      'p',
      { class: 'muted small' },
      'Every date and time in the app is shown in this zone. It does not change ' +
        'when an event is scheduled, only how you see it.',
    ),
    error,
    ok,
    submit,
  ]) as HTMLFormElement;

  form.addEventListener('submit', async (ev) => {
    ev.preventDefault();
    error.hidden = true;
    ok.hidden = true;
    submit.setAttribute('disabled', 'disabled');
    try {
      const updated = await updateProfile({
        firstName: firstName.value.trim(),
        lastName: lastName.value.trim(),
        timeZone: zone.value(),
      });
      done(updated);
    } catch (err) {
      error.textContent = message(err);
      error.hidden = false;
      submit.removeAttribute('disabled');
    }
  });

  return el('section', { class: 'card' }, [form]);
}

// --- Phone -----------------------------------------------------------------

function phoneCard(user: CurrentUser, done: (u: CurrentUser) => void): HTMLElement {
  const error = el('p', { class: 'form-error', hidden: true });
  const note = el('p', { class: 'form-ok', hidden: true });

  const children: HTMLElement[] = [
    el('h2', {}, 'Phone number'),
    el(
      'p',
      { class: 'muted small' },
      'Used for text-message notifications, including the day-of “still in?” ' +
        'check. It is also a way to sign in, which is why it has to be verified.',
    ),
  ];

  // Three states, and they are genuinely different: no number, a number awaiting
  // its code, and a number on file. A single "phone" text field could not tell
  // them apart — which is how the old form managed to be write-only.
  if (user.phonePending) {
    children.push(codeForm(user, error, note, done));
  } else if (user.phone) {
    children.push(onFile(user, error, note, done));
  } else {
    children.push(addForm(error, note, done));
  }

  children.push(error, note);
  return el('section', { class: 'card' }, [el('div', {}, children)]);
}

function addForm(
  error: HTMLElement,
  note: HTMLElement,
  done: (u: CurrentUser) => void,
): HTMLElement {
  const phone = textInput('ac-phone', 'phone', '', 32);
  phone.type = 'tel';
  phone.placeholder = '555 123 4567';
  const submit = el('button', { type: 'submit', class: 'btn' }, 'Send me a code');

  const form = el('form', { method: 'post', action: '/account', novalidate: true }, [
    el('label', { for: 'ac-phone' }, 'Phone number'),
    phone,
    // The A2P 10DLC consent disclosure at the point of opt-in, same wording as
    // /register. Wording must stay in sync with the registered consent text and
    // the SMS program terms in docs/a2p-campaign.md; do not reword independently.
    el('p', { class: 'muted small' }, [
      'By entering your number, you consent to receive text messages from MySocialCal, ',
      'including this verification code. Msg & data rates may apply. Reply STOP to opt out, ',
      'HELP for help. See our ',
      el('a', { href: '/privacy' }, 'Privacy Policy'),
      ' and ',
      el('a', { href: '/terms' }, 'Terms and Conditions'),
      '.',
    ]),
    submit,
  ]) as HTMLFormElement;

  form.addEventListener('submit', async (ev) => {
    ev.preventDefault();
    error.hidden = true;
    note.hidden = true;
    submit.setAttribute('disabled', 'disabled');
    try {
      const result = await startPhoneVerification({ phone: phone.value });
      // A send failure is reported by the endpoint as ok:false on `sent`, not as
      // a thrown error, because the pending number IS saved either way — the code
      // may well have gone out. So surface the message rather than treating a
      // false `sent` as a dead end.
      note.textContent = result.message;
      note.hidden = false;
      const me = await fetchMe();
      done(me);
    } catch (err) {
      error.textContent = message(err);
      error.hidden = false;
      submit.removeAttribute('disabled');
    }
  });

  return form;
}

function codeForm(
  user: CurrentUser,
  error: HTMLElement,
  note: HTMLElement,
  done: (u: CurrentUser) => void,
): HTMLElement {
  const code = textInput('ac-code', 'code', '', 16);
  code.setAttribute('inputmode', 'numeric');
  code.setAttribute('autocomplete', 'one-time-code');
  code.placeholder = '000000';
  const submit = el('button', { type: 'submit', class: 'btn' }, 'Verify');
  const resend = el('button', { type: 'button', class: 'btn btn-secondary' }, 'Send a new code');
  const cancel = el('button', { type: 'button', class: 'btn btn-link' }, 'Cancel');

  const form = el('form', { method: 'post', action: '/account', novalidate: true }, [
    el('p', {}, [`We texted a code to `, el('strong', {}, user.phonePending), '.']),
    el('label', { for: 'ac-code' }, 'Verification code'),
    code,
    el('div', { class: 'form-actions' }, [submit, resend, cancel]),
    el(
      'p',
      { class: 'muted small' },
      'The number is not on your account until the code checks out.',
    ),
  ]) as HTMLFormElement;

  form.addEventListener('submit', async (ev) => {
    ev.preventDefault();
    error.hidden = true;
    note.hidden = true;
    submit.setAttribute('disabled', 'disabled');
    try {
      const updated = await verifyPhone({ code: code.value });
      done(updated);
    } catch (err) {
      // An expired code is its own error code precisely so this branch can point
      // at the resend button instead of sending someone hunting for a typo.
      if (err instanceof ApiClientError && err.code === ErrorCodes.PHONE_CODE_EXPIRED) {
        error.textContent = 'That code has expired. Use “Send a new code”.';
      } else {
        error.textContent = message(err);
      }
      error.hidden = false;
      submit.removeAttribute('disabled');
    }
  });

  resend.addEventListener('click', async () => {
    error.hidden = true;
    note.hidden = true;
    resend.setAttribute('disabled', 'disabled');
    try {
      const result = await startPhoneVerification({ phone: user.phonePending });
      note.textContent = result.message;
      note.hidden = false;
    } catch (err) {
      error.textContent = message(err);
      error.hidden = false;
    }
    resend.removeAttribute('disabled');
  });

  cancel.addEventListener('click', async () => {
    error.hidden = true;
    try {
      // Clears the pending number along with anything on file. The user is in
      // this state precisely because nothing is on file yet.
      done(await removePhone());
    } catch (err) {
      error.textContent = message(err);
      error.hidden = false;
    }
  });

  return form;
}

function onFile(
  user: CurrentUser,
  error: HTMLElement,
  note: HTMLElement,
  done: (u: CurrentUser) => void,
): HTMLElement {
  const isPublic = el('input', { type: 'checkbox', id: 'ac-phone-public' }) as HTMLInputElement;
  isPublic.checked = user.phoneIsPublic;

  const replace = el('button', { type: 'button', class: 'btn btn-secondary' }, 'Use a different number');
  const remove = el('button', { type: 'button', class: 'btn btn-danger' }, 'Remove');

  const rows: HTMLElement[] = [
    el('p', {}, [
      el('strong', {}, user.phone),
      user.phoneVerified ? ' (verified)' : ' (not verified)',
    ]),
  ];

  // A verified number sends nothing until its owner chooses which kinds come by
  // text (SMS is opt-in per type, docs/plan-notifications.md), so say where.
  if (user.phoneVerified) {
    rows.push(
      el('p', { class: 'muted small' }, [
        'Choose which notifications come by text in ',
        el('a', { href: '/settings/notifications' }, 'Notifications settings'),
        '.',
      ]),
    );
  }

  // A number that predates verification. Not an error state and not worth
  // deleting for them: it may be perfectly good, it just has never been proven,
  // and SMS to a wrong number is a silent failure.
  if (!user.phoneVerified) {
    rows.push(
      el(
        'p',
        { class: 'notice' },
        'This number was never verified, so text messages to it may not arrive. ' +
          'Verify it to be sure.',
      ),
    );
  }

  rows.push(
    el('label', { class: 'check', for: 'ac-phone-public' }, [
      isPublic,
      ' Let other members of my groups see this number',
    ]),
    el(
      'p',
      { class: 'muted small' },
      'Group admins can always see it. Unchecked, nobody else can.',
    ),
    el('div', { class: 'form-actions' }, [replace, remove]),
  );

  isPublic.addEventListener('change', async () => {
    error.hidden = true;
    note.hidden = true;
    try {
      await updateProfile({ phoneIsPublic: isPublic.checked });
      note.textContent = 'Saved.';
      note.hidden = false;
    } catch (err) {
      // Put the box back: leaving it showing a state that failed to save is how
      // a user comes to believe their number is private when it is not.
      isPublic.checked = !isPublic.checked;
      error.textContent = message(err);
      error.hidden = false;
    }
  });

  replace.addEventListener('click', async () => {
    error.hidden = true;
    try {
      // Drop it first, which puts the card back into its "add" state. Sending a
      // code for a new number while the old one is still on file would leave two
      // numbers half-attached to one account.
      done(await removePhone());
    } catch (err) {
      error.textContent = message(err);
      error.hidden = false;
    }
  });

  remove.addEventListener('click', async () => {
    error.hidden = true;
    if (!window.confirm('Remove your phone number? Text notifications will stop.')) return;
    try {
      done(await removePhone());
    } catch (err) {
      error.textContent = message(err);
      error.hidden = false;
    }
  });

  return el('div', {}, rows);
}

// --- Password --------------------------------------------------------------

function passwordCard(): HTMLElement {
  const error = el('p', { class: 'form-error', hidden: true });
  const submit = el('button', { type: 'submit', class: 'btn' }, 'Change password and sign out');

  const current = passwordInput('ac-pw-current', 'currentPassword', 'current-password');
  const next = passwordInput('ac-pw-new', 'newPassword', 'new-password');
  const confirm = passwordInput('ac-pw-confirm', 'newPasswordConfirm', 'new-password');

  const form = el('form', { method: 'post', action: '/account', novalidate: true }, [
    el('h2', {}, 'Password'),
    el(
      'p',
      { class: 'notice' },
      'Changing your password signs you out on every device, including this one. ' +
        'You will be asked to log in again straight away.',
    ),
    el('label', { for: 'ac-pw-current' }, 'Current password'),
    current,
    el('label', { for: 'ac-pw-new' }, 'New password'),
    next,
    el('p', { class: 'muted small' }, 'At least 8 characters.'),
    el('label', { for: 'ac-pw-confirm' }, 'Confirm new password'),
    confirm,
    // The two NEW fields only. `current` is a password being typed back, not one
    // being chosen, so revealing it puts a live credential on screen to solve a
    // problem it does not have -- and "Show new password" is what keeps the
    // checkbox from reading as covering all three.
    passwordReveal([next, confirm], 'Show new password'),
    error,
    submit,
  ]) as HTMLFormElement;

  form.addEventListener('submit', async (ev) => {
    ev.preventDefault();
    error.hidden = true;
    submit.setAttribute('disabled', 'disabled');
    try {
      await changePassword({
        currentPassword: current.value,
        newPassword: next.value,
        newPasswordConfirm: confirm.value,
      });
      // The client already dropped the tokens. Routing to /login is what stops
      // the next click rendering a signed-out error on a page that still looks
      // signed in.
      navigate('/login', true);
    } catch (err) {
      error.textContent = message(err);
      error.hidden = false;
      submit.removeAttribute('disabled');
    }
  });

  // card-wide: spans both grid columns, so the two cards that control who can
  // get into the account sit together and the grid tiles without a hole.
  return el('section', { class: 'card card-wide' }, [form]);
}

// --- Delete the account ----------------------------------------------------
//
// App Store Review Guideline 5.1.1(v): an app that lets a user create an account
// must let them initiate deletion from INSIDE the app. A "contact support" link
// is an explicitly named rejection. See docs/plan-ios-ship.md item 1.
//
// Three deliberate choices:
//
//   • It is LAST on the page and visually its own thing. Nobody arrives here
//     looking for it by accident.
//   • It asks for the password AND a typed phrase. The password authorizes (a
//     stolen access token must not be enough); the phrase makes it deliberate.
//     Apple permits re-authentication in this flow.
//   • Owning a group with other members BLOCKS it, and the error lists those
//     groups as links to their Manage pages. That refusal is only honest because
//     transfer-ownership now exists -- until 2026-09-08 it pointed at a screen
//     where promoting a successor was impossible, which Apple treats as blocking
//     deletion outright.

function deleteCard(): HTMLElement {
  const error = el('p', { class: 'form-error', hidden: true });
  const blockers = el('div', { hidden: true });
  const submit = el('button', { type: 'submit', class: 'btn btn-danger' }, 'Delete my account');

  const password = passwordInput('ac-del-pw', 'password', 'current-password');
  const confirm = el('input', {
    id: 'ac-del-confirm',
    name: 'confirm',
    type: 'text',
    autocomplete: 'off',
    // `el()` DROPS a `false` attribute value entirely, so this has to be the
    // string or it renders no attribute at all -- the opposite of what it means.
    spellcheck: 'false',
    required: true,
  }) as HTMLInputElement;

  const form = el('form', { method: 'post', action: '/account', novalidate: true }, [
    el('h2', {}, 'Delete your account'),
    el(
      'p',
      { class: 'notice' },
      'This cannot be undone. You are signed out everywhere, your email address and ' +
        'phone number are removed, and you leave every group. Groups where you are ' +
        'the only member are deleted with you.',
    ),
    el(
      'p',
      { class: 'muted small' },
      'Your first and last name stay on posts, events and results other members can ' +
        'still see, so their own history stays readable. Nothing else about you is kept.',
    ),
    el('label', { for: 'ac-del-pw' }, 'Your password'),
    password,
    el('label', { for: 'ac-del-confirm' }, `Type ${DELETE_ACCOUNT_PHRASE} to confirm`),
    confirm,
    blockers,
    error,
    submit,
  ]) as HTMLFormElement;

  form.addEventListener('submit', async (ev) => {
    ev.preventDefault();
    error.hidden = true;
    blockers.hidden = true;
    blockers.replaceChildren();
    if (confirm.value.trim() !== DELETE_ACCOUNT_PHRASE) {
      error.textContent = `Type ${DELETE_ACCOUNT_PHRASE} in the box to confirm.`;
      error.hidden = false;
      return;
    }
    submit.setAttribute('disabled', 'disabled');
    try {
      await deleteAccount({ confirm: confirm.value.trim(), password: password.value });
      // The chrome caches the signed-in identity, and a cached user survives a
      // cleared token -- `cachedUser !== undefined` short-circuits the refetch.
      // Without this the farewell page renders under a nav belonging to an
      // account that no longer exists. /goodbye is a BARE_PATH as well, so this
      // is belt and braces on purpose: the stale identity must not outlive the
      // account whichever page the user reaches next.
      invalidateChrome();
      // The client already dropped the tokens. /goodbye is a signed-out page, so
      // routing anywhere else would render a signed-in shell for an account that
      // no longer exists.
      navigate('/goodbye', true);
    } catch (err) {
      if (err instanceof ApiClientError && err.code === 'OWNS_GROUPS') {
        blockers.replaceChildren(...groupBlockerList(err.details));
        blockers.hidden = false;
      } else {
        error.textContent = message(err);
        error.hidden = false;
      }
      submit.removeAttribute('disabled');
    }
  });

  return el('section', { class: 'card card-danger' }, [form]);
}

/**
 * The OWNS_GROUPS refusal, rendered as links.
 *
 * Naming the groups is the whole point: "you own a group" sends the user hunting
 * through a group list, and a user who cannot find the blocker cannot delete
 * their account, which is the rejection this flow exists to avoid. Defensive
 * about the shape because `details` is typed `unknown` on the error.
 */
function groupBlockerList(details: unknown): HTMLElement[] {
  const groups = (details as { groups?: BlockingGroup[] } | null)?.groups ?? [];
  if (groups.length === 0) {
    return [
      el(
        'p',
        { class: 'form-error' },
        'You still own a group with other members in it. Transfer it to another member first.',
      ),
    ];
  }
  return [
    el(
      'p',
      { class: 'form-error' },
      groups.length === 1
        ? 'You own a group that has other members in it. Transfer it to another member first, then come back:'
        : 'You own groups that have other members in them. Transfer each one to another member first, then come back:',
    ),
    el(
      'ul',
      {},
      groups.map((g) =>
        el('li', {}, [
          el('a', { href: `/groups/${g.groupId}/manage` }, g.name),
          ` · ${g.memberCount} members`,
        ]),
      ),
    ),
  ];
}

// --- Helpers ---------------------------------------------------------------

function message(err: unknown): string {
  return err instanceof Error ? err.message : 'Something went wrong. Try again.';
}

function textInput(id: string, name: string, value: string, maxlength: number): HTMLInputElement {
  const node = el('input', { id, name, type: 'text', maxlength }) as HTMLInputElement;
  node.value = value;
  return node;
}

function passwordInput(id: string, name: string, autocomplete: string): HTMLInputElement {
  return el('input', {
    id,
    name,
    type: 'password',
    autocomplete,
    required: true,
  }) as HTMLInputElement;
}
